import { Icon } from "@citi-icg-172888/icgds-react";
import React, { useCallback, forwardRef, useEffect, useRef } from "react";
import { useData } from "../../services/context/DataContext";
import _ from "lodash";
import { Layout } from "@citi-icg-172888/icgds-patterns-react";
import { IStep } from "../../types/commonTypes";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LeadSubmission: React.FC = forwardRef((props, ref) => {
  const {
    companyInfo,
    contactInfo,
    businessNeedInfo,
    steps,
    setSteps,
    setIsEditMode,
    setIsBasicInfo,
  } = useData();
  const divRef = useRef<HTMLDivElement>(null);

  const onEditHandler = useCallback(
    (stepParam: number) => {
      const newSteps = _.map(steps, (step: IStep) =>
        step.step === stepParam ? { ...step, active: true } : { ...step, active: false }
      );
      setSteps(newSteps);
      setIsEditMode(true);
      if (stepParam === 1) {
        setIsBasicInfo(true);
      }
    },
    [setSteps, steps, setIsEditMode]
  );

  useEffect(() => {
    if (divRef.current) {
      divRef.current.focus();
      if (window.parent.location.href.includes("contact-us"))
        window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const getValuesForField = (value: string | undefined): React.ReactNode => {
    return <>{value ? <>{value}</> : <>-</>}</>;
  };

  const getValuesForAddress = (value: any | undefined): React.ReactNode => {
    return (
      <>
        {value ? (
          <>{`${value?.streetName} ${value?.city} ${value?.state} ${companyInfo?.global_ultimate_country_name} ${value?.zipcode}`}</>
        ) : (
          <div className="lmn-mt-3">-</div>
        )}
      </>
    );
  };

  return (
    <div className="lmn-mt-md-16px lmn-mt-2xl-16px lmn-mt-xl-16px lmn-mt-lg-16px lmn-mt-sm-16px">
      <div>
        <h5 ref={divRef} className="lmn-text-color-desc lmn-mb-3" tabIndex={-1}>
          Company Information
          <Icon
            tabIndex={0}
            className="lmn-ml-5 lmn-cursor-pointer"
            type="edit"
            role="button"
            alt="icon representing Company Information"
            aria-label="Edit Company Information"
            onClick={() => onEditHandler(1)}
            onKeyDown={(event) => event.key === "Enter" && onEditHandler(1)}
          />
        </h5>
        <div className="lmn-container">
          <ul
            className="lmn-row lmn-p-0 lmn-text-color"
            style={{
              listStyleType: "none",
            }}
            aria-label="Company Information"
            aria-live="polite"
          >
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Company Name</h6>
              <div className="lmn-text-color">
                {getValuesForField(companyInfo?.legalNameOfBusiness)}
              </div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Company Website</h6>
              <div className="lmn-text-color" role="text" aria-label="Company Website">
                {getValuesForField(companyInfo?.website)}
              </div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Primary Address</h6>
              <div className="lmn-text-color">
                {getValuesForAddress(companyInfo?.registerAddress)}
              </div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">
                Other Countries with Subsidiaries or Affiliates
              </h6>
              <div className="lmn-text-color">
                {companyInfo?.operatedCountry?.length
                  ? companyInfo?.operatedCountry?.join(", ")
                  : "-"}
              </div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Industry</h6>
              <div className="lmn-text-color">{getValuesForField(companyInfo?.industry)}</div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Sales Size</h6>
              <div className="lmn-text-color">{getValuesForField(companyInfo?.salesSize)}</div>
            </li>
            {companyInfo?.salesSize === "Pre-Revenue" && (
              <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
                <h6 className="lmn-text-color">Is your company VC/PE backed?</h6>
                <div>
                  <p className="lmn-m-0 lmn-text-color">
                    {companyInfo?.isCompanyVCorPEBacked}
                  </p>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
      <Layout type="divider" layoutClass="cust-border-color" />
      <div>
        <h5 className="lmn-text-color-desc lmn-mb-3">
          Contact Information
          <Icon
            className="lmn-ml-5 lmn-cursor-pointer"
            type="edit"
            alt="icon of Edit Contact Information"
            aria-label="Edit Contact Information"
            role="button"
            tabIndex={0}
            onClick={() => onEditHandler(2)}
            onKeyDown={(event) => event.key === "Enter" && onEditHandler(2)}
          />
        </h5>
        <div className="lmn-container">
          <ul
            className="lmn-p-0 lmn-text-color lmn-row"
            style={{
              listStyleType: "none",
            }}
            aria-label="Contact Information"
            aria-live="polite"
          >
            <li className="lmn-col-12 lmn-mb-3">
              <h6 className="lmn-text-color">Your Role</h6>
              <div className="lmn-text-color">{contactInfo?.role}</div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">First Name</h6>
              <div className="lmn-text-color">{contactInfo?.firstName}</div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Last Name</h6>
              <div className="lmn-text-color">{contactInfo?.lastName}</div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Email ID</h6>
              <div className="lmn-text-color">{contactInfo?.email}</div>
            </li>
            <li className="lmn-col-sm lmn-col-md-6 lmn-mb-3">
              <h6 className="lmn-text-color">Country Code & Phone Number</h6>
              <div className="lmn-text-color">
                {contactInfo?.countryCode + " " + contactInfo?.mobile}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <Layout type="divider" layoutClass="cust-border-color" />
      <div className="lmn-mb-md-16px lmn-mb-2xl-16px lmn-mb-xl-16px lmn-mb-lg-16px lmn-mb-sm-16px">
        <h5 className="lmn-text-color-desc lmn-my-5">
          Business needs
          <Icon
            className="lmn-ml-5 lmn-cursor-pointer"
            type="edit"
            tabIndex={0}
            role="button"
            alt="icon of Edit Business Needs"
            aria-label="Edit Business Needs"
            onClick={() => onEditHandler(3)}
            onKeyDown={(event) => event.key === "Enter" && onEditHandler(3)}
          />
        </h5>
        <div>
          <ul
            className="lmn-p-0 lmn-text-color"
            style={{ listStyleType: "none" }} // Inline style to remove bullets
            aria-label="Business Needs"
            aria-live="polite"
          >
            {Array.isArray(businessNeedInfo?.businessNeeds) &&
              businessNeedInfo?.businessNeeds.map((bn) => (
                <li>
                  <h6 className="lmn-text-color">{bn.title.toString()}</h6>
                  <div
                    className="busniess-needs-text"
                    style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                  >
                    {bn.description.toString()}
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
});

export default LeadSubmission;
